import React, { useCallback, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Box, Button, DialogActions, DialogContent, Grid, IconButton, Skeleton, Typography } from '@mui/material'
import Lock from '@mui/icons-material/Lock'
import LockOpen from '@mui/icons-material/LockOpen'
import { Dialog, DialogTitleWithCloseButton } from '@carsys/ui'
import { Checkbox, TextField } from '@carsys/form'
import { FormValues } from './Form'
import { useGetWholesalerWithServices } from '../hooks/useGetWholesalerWithServices'
import { useGetWholesalerOrderSystem } from '../hooks/useGetWholesalerOrderSystem'
import { OrderSystemService } from '@cbgms/api'
import { useEnumTranslation } from '@cbgms/base/enums/use-enum-translation'

const desiredServicesSort: string[] = [
  'Order',
  'PriceStock',
  'RMA',
  'SearchInCatalog',
  'IFrame',
  'SearchInOrderSystem',
  'LicencePlate',
  'Tyres'
]
const servicesSortFunc = (a: [string, any], b: [string, any]) => {
  const orderForIndexVals = desiredServicesSort.slice(0).reverse()
  const aIndex = -orderForIndexVals.indexOf(a[0])
  const bIndex = -orderForIndexVals.indexOf(b[0])
  const result = aIndex - bIndex
  if (result !== 0) {
    return result
  }
  return a[0].localeCompare(b[0])
}
export const ServiceFields: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { te } = useEnumTranslation()
  const hintText = useCallback(
    (key: string) => {
      const translationKey = `wholesale.service_hint_text_${key}`
      if (i18n.exists(translationKey)) {
        return i18n.t(translationKey)
      }
      return null
    },
    [i18n]
  )
  const [confirmation, setConfirmation] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const { values, setFieldValue } = useFormikContext<FormValues>()
  const { data: serviceData, isInitialLoading: serviceLoading } = useGetWholesalerWithServices(
    values.UUID,
    values.Country,
    values.OrderSystem
  )
  const { data: orderSystemData, isLoading: orderSystemLoading } = useGetWholesalerOrderSystem(
    values.Country,
    values.OrderSystem,
    values.OrderSystemSettings
  )
  const sortedServicesKeyValuePairs = useMemo(
    () => Object.entries(orderSystemData?.WholesalerOrderSystem?.Services || []).sort(servicesSortFunc),
    [orderSystemData?.WholesalerOrderSystem?.Services]
  )

  React.useEffect(() => {
    if (serviceData) {
      setFieldValue('ServiceToggles', serviceData.ServiceToggles)
      setFieldValue('ServiceConfig', serviceData.ServiceConfig)
    }
  }, [serviceData])

  if (serviceLoading || orderSystemLoading || !orderSystemData) return <LoadingSkeleton />
  return (
    <>
      <ServiceDialog
        open={open}
        handleClose={() => setOpen(false)}
        handleConfirm={() => {
          setConfirmation(!confirmation)
          setOpen(false)
        }}
      />

      <Grid container direction='row' columnSpacing={2} paddingRight={2}>
        <Grid item xs={11}>
          <Typography variant='h4'>{t('wholesale.services_title')}</Typography>
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={() => setOpen(true)} size='large'>
            {confirmation ? <LockOpen /> : <Lock />}
          </IconButton>
        </Grid>

        {sortedServicesKeyValuePairs.map(([key, value]) => (
          <Grid item xs={12} sm={6} md={6} key={key}>
            <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
              <Box>
                <Checkbox
                  name={`ServiceToggles.${key}`}
                  label={te('EnumOrderSystemService', key)}
                  title={hintText(key)}
                  disabled={!value || !confirmation}
                />
              </Box>
              <Box sx={{ flex: '2 1 auto' }}>
                {key === OrderSystemService.LicencePlate && (
                  <TextField
                    size='small'
                    fullWidth
                    name={`ServiceConfig.${OrderSystemService.LicencePlate}`}
                    label={t(`wholesaler.service_config_LicencePlate`)}
                    disabled={!values.ServiceToggles[OrderSystemService.LicencePlate] || !confirmation}
                  />
                )}
                {key === OrderSystemService.Tyres && (
                  <TextField
                    size='small'
                    fullWidth
                    name={`ServiceConfig.${OrderSystemService.Tyres}`}
                    label={t(`wholesaler.service_config_Tyres`)}
                    disabled={!values.ServiceToggles[OrderSystemService.Tyres] || !confirmation}
                  />
                )}
                {key === OrderSystemService.IFrame && (
                  <TextField
                    size='small'
                    fullWidth
                    name='IFrameURL'
                    label={t('wholesale.iframe_url')}
                    disabled={!values.ServiceToggles[OrderSystemService.IFrame] || !confirmation}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </>
  )
}

const ServiceDialog = ({ open, handleClose, handleConfirm }: { open: boolean; handleClose: () => void; handleConfirm: () => void }) => {
  const dragID = 'drag-confirmation-dialog'
  const { t } = useTranslation()

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitleWithCloseButton onClose={handleClose} draggableIDHandler={dragID}>
        {t('Confirmation.ConfirmTitle')}
      </DialogTitleWithCloseButton>
      <DialogContent>{t('Confirmation.UrgentText')}</DialogContent>
      <DialogActions>
        <Button color='primary' variant='contained' onClick={() => handleConfirm()}>
          {t('Confirmation.ConfirmAction')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const LoadingSkeleton = () => {
  const height = 30
  return (
    <Grid container direction='row' spacing={2}>
      <Grid item xs={6}>
        <Skeleton height={height} variant='rectangular' />
      </Grid>
      <Grid item xs={6}>
        <Skeleton height={height} variant='rectangular' />
      </Grid>
      <Grid item xs={6}>
        <Skeleton height={height} variant='rectangular' />
      </Grid>
      <Grid item xs={6}>
        <Skeleton height={height} variant='rectangular' />
      </Grid>
      <Grid item xs={6}>
        <Skeleton height={height} variant='rectangular' />
      </Grid>
      <Grid item xs={6}>
        <Skeleton height={height} variant='rectangular' />
      </Grid>
    </Grid>
  )
}
export default ServiceFields
